import { startsWith } from 'ramda';

import {
    COMPLETION_NAVIGATION_VALUES,
    ASPECT,
    INITIAL_NODE_NAME,
    FINISH_NODE_NAME,
    TARGET_RESOLVE,
    ELEMENT_PATHS,
    PATH,
    LOCATION_HREF,
    SUSPEND_NODE_NAME,
    NODE,
    QUERY_EDITOR_PARAM,
    LOCAL_STORAGE_EDITOR_PARAM,
} from './ModelConstant';
import { parseUrl } from './utils/UrlUtils';
import { localeCompare } from './utils/FpUtils';

// it is map because of easier access and comparison
const KNOWN_QUERY_PARAMS = {
    [QUERY_EDITOR_PARAM.TOURID]: {},
    [QUERY_EDITOR_PARAM.LANG]: {},
    [QUERY_EDITOR_PARAM.COLLECTORID]: {},
    [QUERY_EDITOR_PARAM.PREVIEW]: {},
};

/**
 * @param {string} _queryString
 * @returns {Array.<string>}
 */
export const parseUrlQuery = (_queryString) => {
    if (_queryString.length === 0) {
        return [];
    }
    const decodedQueryString = decodeURIComponent(_queryString);
    const queryString = startsWith('?', decodedQueryString)
        ? decodedQueryString.substring(1)
        : decodedQueryString;
    const split = queryString.split('&');
    const result = split.filter((queryItem) => {
        const queryParamValue = queryItem.split('=');
        return !KNOWN_QUERY_PARAMS[queryParamValue[0]];
    });
    return result;
};

/**
 * @param {string} query
 * @returns {string}
 */
export const stripEditorParamsFromQuery = (query) => decodeURIComponent(query)
    .replace(new RegExp(`([?|&]${QUERY_EDITOR_PARAM.TOURID}=[a-z0-9-]*)`), '')
    .replace(new RegExp(`([?|&]${QUERY_EDITOR_PARAM.COLLECTORID}=[a-z0-9-]*)`), '')
    .replace(new RegExp(`([?|&]${QUERY_EDITOR_PARAM.APPLICATION_AUGMENTATION_ID}=[a-z0-9-]*)`), '')
    .replace(new RegExp(`([?|&]${QUERY_EDITOR_PARAM.PREVIEW})`), '')
    .replace(new RegExp(`([?|&]${QUERY_EDITOR_PARAM.LANG}=[a-z-]{2,5})`), '')
    .replace(/^&/, '?');

/**
 * @param {string} url
 * @returns {string}
 */
export const strippedKnownQuery = (url) =>
    url.replace(/([^?]*)(\?[^#^/]*)(.*)/, (match, host, query, hash) => `${host}${stripEditorParamsFromQuery(query)}${hash}`);


/**
 * @param {string} sourceQuery
 * @param {string} targetQuery
 *
 * @returns {boolean}
 */
export const isQueryEqual = (sourceQuery, targetQuery) =>
    parseUrlQuery(sourceQuery).sort().toString() === parseUrlQuery(targetQuery).sort().toString();
/**
 * @param {string} text
 * @param {string} pattern
 *
 * @returns {boolean}
 */
export const isPatternMatched = (text, pattern) => new RegExp(pattern).test(text);

/**
 * @param {string} sourceUrl
 * @param {string} targetUrl
 * @param {boolean} ignoreQueryParameters
 *
 * @returns {string}
 */
const navigationTypeByUrl = (sourceUrl, targetUrl, ignoreQueryParameters) => {
    const source = parseUrl(sourceUrl);
    const target = parseUrl(targetUrl);

    const isHostnameEqual =
        localeCompare(target.hostname, source.hostname, { sensitivity: 'base' }) === 0;
    const isPathnameEqual =
        localeCompare(target.pathname, source.pathname, { sensitivity: 'base' }) === 0;
    const isPortEqual = localeCompare(target.port, source.port, { sensitivity: 'base' }) === 0;
    const isSearchEqual = ignoreQueryParameters || isQueryEqual(source.search, target.search);
    const isHashEqual = localeCompare(target.hash, source.hash, { sensitivity: 'base' }) === 0;

    const softNavigation =
        isHostnameEqual && isPathnameEqual && isPortEqual && isSearchEqual && !isHashEqual;
    const hardNavigation = !isHostnameEqual || !isPathnameEqual || !isPortEqual || !isSearchEqual;

    return softNavigation
        ? COMPLETION_NAVIGATION_VALUES.SOFT
        : hardNavigation
        ? COMPLETION_NAVIGATION_VALUES.HARD
        : COMPLETION_NAVIGATION_VALUES.NO;
};

/**
 * @param {string} sourceUrl
 * @param {string} targetUrl
 * @param {string} pattern
 *
 * @returns {string}
 */

export const patternTransformedUrl = (sourceUrl, targetUrl, pattern) => {
    const sourceUrlMatch = sourceUrl.match(pattern)[0];
    const targetUrlMatch = targetUrl.match(pattern)[0];

    return targetUrl.replace(targetUrlMatch, sourceUrlMatch);
};

const transformStep = (sourceUrl, targetUrl, pattern) =>
    isPatternMatched(sourceUrl, pattern) && isPatternMatched(targetUrl, pattern)
        ? patternTransformedUrl(sourceUrl, targetUrl, pattern)
        : targetUrl;

export const transformUrl = (sourceUrl, targetUrl, urlPattern, multiTenant) => {
    const isMultiTenant =
        multiTenant &&
        multiTenant.pattern &&
        multiTenant.pattern !== LOCATION_HREF.NO_PATTERN &&
        multiTenant.pattern.length;
    const hasUrlPattern = urlPattern && urlPattern !== LOCATION_HREF.NO_PATTERN;

    const multiTenantPatternTransformStep = isMultiTenant
        ? transformStep(sourceUrl, targetUrl, multiTenant.pattern)
        : targetUrl;

    const urlPatternTransformStep = hasUrlPattern
        ? transformStep(sourceUrl, multiTenantPatternTransformStep, urlPattern)
        : multiTenantPatternTransformStep;

    return urlPatternTransformStep;
};

/**
 * @param {string} sourceUrl
 * @param {string} targetUrl
 * @param {string} pattern
 *
 * @returns {string}
 */
const navigationTypeByPatternTransformedUrl = (sourceUrl, targetUrl, pattern, ignoreQueryParameters) => {
    const matchedTargetUrl =
        isPatternMatched(sourceUrl, pattern) && isPatternMatched(targetUrl, pattern)
            ? patternTransformedUrl(sourceUrl, targetUrl, pattern)
            : targetUrl;
    return navigationTypeByUrl(sourceUrl, matchedTargetUrl, ignoreQueryParameters);
};

const navigationTypeByPattern = (sourceUrl, pattern) =>
    isPatternMatched(sourceUrl, pattern)
        ? COMPLETION_NAVIGATION_VALUES.NO
        : COMPLETION_NAVIGATION_VALUES.WAIT;

/**
 * @param {string} sourceUrl
 * @param {Href} targetHref
 * @param {Object} metadata
 * @param {boolean} metadata.ignoreQueryParameters
 * @param {boolean} metadata.shouldNavigate
 *
 * @returns {string}
 */

const commonLocationNavigationType = (
    sourceUrl,
    targetHref,
    { ignoreQueryParameters, shouldNavigate }
) => {
    const strippedUrl = strippedKnownQuery(sourceUrl);
    return targetHref.pattern && targetHref.pattern !== LOCATION_HREF.NO_PATTERN
        ? shouldNavigate
            ? navigationTypeByPatternTransformedUrl(
                  strippedUrl,
                  targetHref.url,
                  targetHref.pattern,
                  ignoreQueryParameters
              )
            : navigationTypeByPattern(strippedUrl, targetHref.pattern)
        : navigationTypeByUrl(strippedUrl, targetHref.url, ignoreQueryParameters);
};

/**
 * @param {string} sourceUrl
 * @param {Href} targetHref
 * @param {Object} metadata
 * @param {boolean} metadata.ignoreQueryParameters
 * @param {boolean} metadata.shouldNavigate
 * @param {string} pattern
 *
 * @returns {string}
 */

const multiTenantLocationNavigationType = (
    sourceUrl,
    targetHref,
    { ignoreQueryParameters, shouldNavigate },
    pattern
) =>
    commonLocationNavigationType(
        sourceUrl,
        {
            ...targetHref,
            url:
                isPatternMatched(sourceUrl, pattern) && isPatternMatched(targetHref.url, pattern)
                    ? patternTransformedUrl(sourceUrl, targetHref.url, pattern)
                    : targetHref.url,
        },
        { ignoreQueryParameters, shouldNavigate }
    );

/**
 * @param {string} sourceUrl
 * @param {Href} targetHref
 * @param {Object} metadata
 * @param {boolean} metadata.ignoreQueryParameters
 * @param {boolean} metadata.shouldNavigate
 * @param {MultiTenant} [multiTenant]
 *
 * @returns {string}
 */

export const locationNavigationType = (
    sourceUrl,
    targetHref,
    { ignoreQueryParameters, shouldNavigate },
    multiTenant
) => {
    const decodedSourceUrl = decodeURI(sourceUrl);
    const decodedTargetHref = {
        ...targetHref,
        url: decodeURI(targetHref.url),
    };

    return multiTenant && multiTenant.pattern && multiTenant.pattern !== LOCATION_HREF.NO_PATTERN
        ? multiTenantLocationNavigationType(
              decodedSourceUrl,
              decodedTargetHref,
              { ignoreQueryParameters, shouldNavigate },
              multiTenant.pattern
          )
        : commonLocationNavigationType(decodedSourceUrl, decodedTargetHref, {
              ignoreQueryParameters,
              shouldNavigate,
          });
};

/**
 * Returns value from URL query by its name
 */
export const getQueryValue = (param = '') => {
    let value;
    const query = window.decodeURIComponent(window.location.search);
    const index = query.indexOf(param);

    if (index >= 0) {
        let end = query.indexOf('&', index + 1);
        end = end < 0 ? query.length : end;
        value = query.substring(index + param.length + 1, end);
    }

    return value;
};

export const urlMatch = (regexp = '') => {
    const result = window.location.href.match(regexp);
    return result && result[1];
};

export const getCurrentLayerId = () => window.localStorage.getItem(LOCAL_STORAGE_EDITOR_PARAM.GUIDEID);

export const isFirstStep = (stepId, guide) => stepId === guide.graph.nodes[0].id;

const createVirtualNodeId = (nodeOrNodeId, type) =>
    `virtual-${type}-${typeof nodeOrNodeId === 'string' ? nodeOrNodeId : nodeOrNodeId.id}`;

const nodeId = (nodeOrNodeId) => (typeof nodeOrNodeId === 'string' ? nodeOrNodeId : nodeOrNodeId.id);

const NODE_TYPE_PREFIX = {
    ACTION: 'action-',
    DECISION: 'decision-',
    STEP: 'step-',
};

export const actionNodeId = (nodeOrNodeId) => `${NODE_TYPE_PREFIX.ACTION}${nodeId(nodeOrNodeId)}`;
export const decisionNodeId = (nodeOrNodeId) => `${NODE_TYPE_PREFIX.DECISION}${nodeId(nodeOrNodeId)}`;
export const stepNodeId = (nodeOrNodeId) => `${NODE_TYPE_PREFIX.STEP}${nodeId(nodeOrNodeId)}`;
export const forceNodeId = (nodeOrNodeId) => `force-${nodeId(nodeOrNodeId)}`;
export const virtualStepNodeId = (nodeOrNodeId) => `virtual-${nodeId(nodeOrNodeId)}`;
export const waitNodeId = (nodeOrNodeId) => createVirtualNodeId(nodeOrNodeId, 'wait');
export const kickWaitNodeId = (nodeOrNodeId) => createVirtualNodeId(nodeOrNodeId, 'wait-kick');
export const commandWaitNodeId = (nodeOrNodeId) => createVirtualNodeId(nodeOrNodeId, 'wait-command');
export const completionWaitNodeId = (nodeOrNodeId) =>
    createVirtualNodeId(nodeOrNodeId, 'wait-completion');
export const stepTransitionEndNodeId = (nodeOrNodeId) =>
    waitNodeId(`transitionStepEnd-${nodeId(nodeOrNodeId)}`);

export const waitNoGuideRunningNode = (nodeOrNodeId) =>
    `${NODE.WAIT_NO_GUIDE_RUNNING}-${nodeId(nodeOrNodeId)}`;

const toBeResolvedNodeId = (nodeOrNodeId, type) => `${type}${nodeId(nodeOrNodeId)}`;
export const toBeResolvedStepId = (nodeOrNodeId) =>
    toBeResolvedNodeId(nodeOrNodeId, TARGET_RESOLVE.STEP);
export const toBeResolvedKickOrStepId = (nodeOrNodeId) =>
    toBeResolvedNodeId(nodeOrNodeId, TARGET_RESOLVE.KICK_OR_STEP);
export const toBeResolvedKickOrStepOrDecisionId = (nodeOrNodeId) =>
    toBeResolvedNodeId(nodeOrNodeId, TARGET_RESOLVE.KICK_OR_STEP_OR_DECISION);
export const toBeResolvedSystemCommandOrKickOrStepId = (nodeOrNodeId) =>
    toBeResolvedNodeId(nodeOrNodeId, TARGET_RESOLVE.SYSTEM_COMMAND_OR_KICK_OR_STEP);
export const toBeResolvedCompletionId = (nodeOrNodeId) =>
    toBeResolvedNodeId(nodeOrNodeId, TARGET_RESOLVE.COMPLETION);

const isVirtualNodeOfType = (id, type) => startsWith(`virtual-${type}-`, id);
export const isDecisionNode = (id) => startsWith(NODE_TYPE_PREFIX.DECISION, id);
export const isActionNode = (id) => startsWith(NODE_TYPE_PREFIX.ACTION, id);
export const isStepNode = (id) => startsWith(NODE_TYPE_PREFIX.STEP, id);
export const isVirtualStepNode = (id) => isVirtualNodeOfType(id, 'step');
export const isVirtualNode = (id) => startsWith('virtual-', id);
export const isWaitNode = (id) => isVirtualNodeOfType(id, 'wait');
export const isKickWaitNode = (id) => isVirtualNodeOfType(id, 'wait-kick');
export const isCompletionWaitNode = (id) => isVirtualNodeOfType(id, 'wait-completion');
export const isStepTransitionEndNode = (id) => isVirtualNodeOfType(id, 'wait-transitionStepEnd');
export const isNavigationNode = (id) => isVirtualNodeOfType(id, 'navigation');
export const isWaitNoGuideRunningNode = (id) => isVirtualNodeOfType(id, 'wait-no-guide-running');
export const isFinishNode = (id) => id === FINISH_NODE_NAME;
export const isSuspendNode = (id) => id === SUSPEND_NODE_NAME;
export const isInitialNode = (id) => id === INITIAL_NODE_NAME;

export const edgeSourceKey = (source) =>
    isStepNode(source) || isVirtualStepNode(source) || isActionNode(source) || isDecisionNode(source)
        ? source.replace(/(.*step|action|decision).*/, '$1')
        : source.replace(/(.*?)(?:-force)*?-(step|action|decision)(.*)/, '$1');

export const edgeSourceTargetKey = (source, target) =>
    `${edgeSourceKey(source)}:${edgeSourceKey(target)}`;

const STEP_TYPES = [NODE_TYPE_PREFIX.ACTION, NODE_TYPE_PREFIX.DECISION, NODE_TYPE_PREFIX.STEP];

export const getStepIdFromNode = (nodeId) => {
    const type = STEP_TYPES.find((type) => nodeId?.indexOf(type) >= 0);
    return nodeId?.substring(nodeId.indexOf(type));
};

export const triggerJumpTo = (stepId) => `${ASPECT.JUMP_TO}:${stepId}`;

/**
 * @param {NodeLocation} location
 * @param {string} locale
 *
 * @return {NodeLocation}
 */
export const localizedLocation = (location, locale) => ({
    ...location,
    paths: location.paths.map((locationPath) =>
        locationPath.id === ELEMENT_PATHS.XPATH
            ? {
                  ...locationPath,
                  path:
                      locationPath.path[locale] || locationPath.path[locale] === ''
                          ? locationPath.path[locale]
                          : PATH.NOT_RECORDED_YET,
              }
            : locationPath
    ),
});

export const helpId = (guideId) => `guide-${guideId}`;

export const virtualHelpId = (guideId) => `virtual-guide-${guideId}`;

/**
 * @param {string} name
 * @param {string} themeId
 */
export const themeClassName = (name, themeId) => `yeself-${name}-${themeId}`;

export const guideNodeFinishCommandValue = (guideId, nodeId) => `guide-${guideId}:node-${nodeId}`;

export const isGuideFinishCommand = (command) => /^guide-[0-9a-z-]+:node-[0-9a-z-]+$/.test(command);
